import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { reportAPI } from '../../services/api';
import toast from 'react-hot-toast';
import SummaryCards from './SummaryCards';
import Charts from './Charts';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    totalVisits: 0,
    totalSales: 0,
    totalWorkPlans: 0,
    monthlySales: 0,
    todayVisits: 0,
    upcomingPlans: 0
  });
  const [recentVisits, setRecentVisits] = useState([]);
  const [recentSales, setRecentSales] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await reportAPI.getDashboard();
      const data = response.data.data;
      setSummary(data.summary);
      setRecentVisits(data.recentVisits || []);
      setRecentSales(data.recentSales || []);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container max-w-7xl mx-auto px-4 py-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.fullName?.split(' ')[0] || 'User'}!
        </h1>
        <p className="text-gray-600 mt-1">
          Here's what's happening in your territory today.
        </p>
      </div>

      <SummaryCards summary={summary} />
      
      <div className="mt-8">
        <Charts />
      </div>

      {/* Recent Activity */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent Visits</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {recentVisits.length === 0 ? (
              <p className="px-6 py-4 text-gray-500">No recent visits</p>
            ) : (
              recentVisits.map((visit) => (
                <div key={visit.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{visit.visitor_name}</p>
                      <p className="text-sm text-gray-600">
                        {visit.visitType?.type_name || 'Unknown'} • {visit.region?.name || 'Unknown'}
                      </p>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(visit.visit_date).toLocaleDateString()}
                    </span>
                  </div>
                  {visit.remark && (
                    <p className="text-sm text-gray-500 mt-1">{visit.remark}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent Sales</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {recentSales.length === 0 ? (
              <p className="px-6 py-4 text-gray-500">No recent sales</p>
            ) : (
              recentSales.map((sale) => (
                <div key={sale.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">
                        {sale.product?.name || 'Unknown Product'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {sale.vendor?.name || 'Unknown Vendor'} • {sale.quantity} units
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        ₹{parseFloat(sale.total_price).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(sale.sale_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {sale.remark && (
                    <p className="text-sm text-gray-500 mt-1">{sale.remark}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;